import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  OpenWebActionBrowserResponse,
  WebActionBrowserFallbackRequest,
  WebActionBrowserLiveViewResponse,
  WebActionBrowserState,
  WebActionBrowserStateResponse,
  WebActionFallbackReason,
} from '@dreamai/types';
import api from '../../../../api';
import './HostedBrowserPanel.scss';

/**
 * Hosted (Browserbase) browser for a human web_action task.
 *
 * The browser belongs to the customer (their saved profile), not to the
 * operator, so nothing from another customer can appear in it. It is created
 * only when the operator clicks "Open hosted browser"; reopening the task
 * page reconnects to the same browser. If the hosted browser cannot do the
 * job (bot wall, files, outage) the operator moves the WHOLE task to a fresh
 * incognito window of their own via the fallback action — that ends the
 * hosted browser and is logged with a reason.
 *
 * The live-view URL grants control of the browser: it lives only in React
 * state here and is fetched per mount from the authenticated route.
 *
 * The backend opens the task URL in the hosted browser itself; the copy button
 * is only for the incognito fallback.
 *
 * Rollout is server-side (`WEB_ACTION_HOSTED_BROWSER`): when the state route
 * answers `enabled: false` for this operator the panel renders nothing and the
 * task works exactly as before.
 */

interface HostedBrowserPanelProps {
  taskId: string;
  targetUrl?: string | null;
  readOnly?: boolean;
}

const HEARTBEAT_MS = 60_000;
const EXPIRY_WARNING_MS = 15 * 60_000;
/** Another open for this task is in flight: give it a moment, then join it. */
const OPEN_RACE_RETRY_MS = 1_500;
const OPEN_RACE_RETRIES = 3;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const FALLBACK_REASONS: { value: WebActionFallbackReason; label: string }[] = [
  {
    value: 'bot_blocked',
    label: 'Site blocks the hosted browser (CAPTCHA / bot wall)',
  },
  {
    value: 'site_broken_in_hosted',
    label: 'Site does not work properly in the hosted browser',
  },
  { value: 'file_transfer', label: 'Need to upload or download a file' },
  {
    value: 'hosted_unavailable',
    label: 'Hosted browser would not start / keeps dropping',
  },
  { value: 'capacity', label: 'No hosted browser available (at capacity)' },
  { value: 'other', label: 'Other (explain below)' },
];

const FALLBACK_REASON_LABELS: Record<string, string> = Object.fromEntries(
  FALLBACK_REASONS.map((r) => [r.value, r.label]),
);

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'expired';
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m left`;
  if (totalMin >= 1) return `${m} min left`;
  return 'under a minute left';
}

function errorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { error?: string } } })
      .response;
    if (response?.data?.error) return response.data.error;
  }
  return fallback;
}

function errorCode(err: unknown): string | null {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { code?: string } } })
      .response;
    return response?.data?.code ?? null;
  }
  return null;
}

const HostedBrowserPanel: React.FC<HostedBrowserPanelProps> = ({
  taskId,
  targetUrl,
  readOnly = false,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [state, setState] = useState<WebActionBrowserState | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [liveViewUrl, setLiveViewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<
    'open' | 'reconnect' | 'relaunch' | 'release' | 'fallback' | null
  >(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [atCapacity, setAtCapacity] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [fallbackReason, setFallbackReason] =
    useState<WebActionFallbackReason>('bot_blocked');
  const [fallbackNote, setFallbackNote] = useState('');
  const [copied, setCopied] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const loadState = useCallback(async () => {
    try {
      const response = await api.get<WebActionBrowserStateResponse>(
        `/human-tasks/${taskId}/browser`,
        await authHeaders(),
      );
      if (response.data.success && response.data.enabled === false) {
        setEnabled(false);
        return null;
      }
      if (response.data.success && response.data.state) {
        setState(response.data.state);
        return response.data.state;
      }
      setError(response.data.error ?? 'Could not load hosted browser state');
    } catch (err) {
      if (errorCode(err) === 'hosted_browser_disabled') {
        setEnabled(false);
        return null;
      }
      setError(errorMessage(err, 'Could not load hosted browser state'));
    } finally {
      setLoading(false);
    }
    return null;
  }, [taskId, authHeaders]);

  const fetchLiveView = useCallback(async () => {
    try {
      const response = await api.get<WebActionBrowserLiveViewResponse>(
        `/human-tasks/${taskId}/browser/live-view`,
        await authHeaders(),
      );
      setLiveViewUrl(response.data.liveViewUrl ?? null);
      return response.data.liveViewUrl ?? null;
    } catch {
      setLiveViewUrl(null);
      return null;
    }
  }, [taskId, authHeaders]);

  // Initial load; if a browser is already running for this task (page was
  // reopened after a tab close), reconnect to it straight away.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loaded = await loadState();
      if (cancelled || !loaded) return;
      if (loaded.session?.status === 'running' && !readOnly) {
        await fetchLiveView();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadState, fetchLiveView, readOnly]);

  const sessionRunning = state?.session?.status === 'running';
  const hosted = state?.executionMode !== 'operator_incognito';

  // Heartbeat while the operator can actually see the live view — keeps the
  // idle reaper away and refreshes expiry / persistence info. The task tabs
  // hide inactive panes with display:none rather than unmounting them, so a
  // hidden panel (offsetParent === null) or a background browser tab must not
  // count as activity.
  useEffect(() => {
    if (!sessionRunning || !liveViewUrl || readOnly) return;
    let cancelled = false;
    const visible = () =>
      document.visibilityState === 'visible' &&
      panelRef.current?.offsetParent !== null;
    const beat = async () => {
      if (!visible()) return;
      try {
        const response = await api.post<WebActionBrowserStateResponse>(
          `/human-tasks/${taskId}/browser/heartbeat`,
          {},
          await authHeaders(),
        );
        if (!cancelled && response.data.state) {
          setState(response.data.state);
          if (response.data.state.session?.status !== 'running') {
            setLiveViewUrl(null);
          }
        }
      } catch {
        // Next tick retries; a dead session surfaces via state refresh.
      }
    };
    beat();
    const id = window.setInterval(beat, HEARTBEAT_MS);
    document.addEventListener('visibilitychange', beat);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', beat);
    };
  }, [sessionRunning, liveViewUrl, readOnly, taskId, authHeaders]);

  const sessionId = state?.session?.id ?? null;
  useEffect(() => {
    if (!sessionRunning) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [sessionRunning, sessionId]);

  const openBrowser = useCallback(
    async (forceRelaunch: boolean) => {
      setBusy(forceRelaunch ? 'relaunch' : 'open');
      setError(null);
      setAtCapacity(false);
      try {
        const response = await api.post<OpenWebActionBrowserResponse>(
          `/human-tasks/${taskId}/browser/open`,
          { forceRelaunch },
          await authHeaders(),
        );
        if (response.data.success && response.data.state) {
          setState(response.data.state);
          setLiveViewUrl(response.data.liveViewUrl ?? null);
          if (!response.data.liveViewUrl) {
            setError(
              'The browser started but its live view could not be loaded. Try Reconnect.',
            );
          }
        } else {
          setError(response.data.error ?? 'Could not open the hosted browser');
        }
      } catch (err) {
        const code = errorCode(err);
        if (code === 'open_in_progress') {
          // A duplicate click / second tab is already opening this task's
          // browser: wait for its row and reconnect instead of failing.
          for (let attempt = 0; attempt < OPEN_RACE_RETRIES; attempt++) {
            await sleep(OPEN_RACE_RETRY_MS);
            const latest = await loadState();
            if (latest?.session?.status === 'running') {
              const url = await fetchLiveView();
              if (url) return;
            }
          }
          setError(
            'The hosted browser is still starting in another tab. Use Reconnect in a moment.',
          );
          return;
        }
        if (code === 'hosted_browser_disabled') {
          setEnabled(false);
          return;
        }
        if (code === 'capacity_exhausted') {
          setAtCapacity(true);
          setError(
            'All hosted browsers are in use right now. Idle ones are being cleaned up — try again in a minute, or continue in your own incognito window.',
          );
        } else {
          setError(errorMessage(err, 'Could not open the hosted browser'));
        }
        await loadState();
      } finally {
        setBusy(null);
      }
    },
    [taskId, authHeaders, loadState, fetchLiveView],
  );

  const reconnect = useCallback(async () => {
    setBusy('reconnect');
    setError(null);
    try {
      const url = await fetchLiveView();
      if (url) {
        await loadState();
        return;
      }
      // No live view. If our record still says the browser is running, the
      // provider session died underneath us — open() relaunches it with the
      // same profile. If it was closed or moved to incognito meanwhile, leave
      // that decision alone.
      const latest = await loadState();
      if (
        latest &&
        latest.executionMode !== 'operator_incognito' &&
        latest.session?.status === 'running'
      ) {
        await openBrowser(false);
      }
    } finally {
      setBusy((current) => (current === 'reconnect' ? null : current));
    }
  }, [fetchLiveView, openBrowser, loadState]);

  const release = useCallback(async () => {
    setBusy('release');
    setError(null);
    try {
      const response = await api.post<WebActionBrowserStateResponse>(
        `/human-tasks/${taskId}/browser/release`,
        {},
        await authHeaders(),
      );
      if (response.data.success && response.data.state) {
        setState(response.data.state);
        setLiveViewUrl(null);
      } else {
        setError(response.data.error ?? 'Could not close the hosted browser');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not close the hosted browser'));
    } finally {
      setBusy(null);
    }
  }, [taskId, authHeaders]);

  const submitFallback = useCallback(async () => {
    setBusy('fallback');
    setError(null);
    try {
      const body: WebActionBrowserFallbackRequest = {
        reason: fallbackReason,
        ...(fallbackNote.trim() ? { note: fallbackNote.trim() } : {}),
      };
      const response = await api.post<WebActionBrowserStateResponse>(
        `/human-tasks/${taskId}/browser/fallback`,
        body,
        await authHeaders(),
      );
      if (response.data.success && response.data.state) {
        setState(response.data.state);
        setLiveViewUrl(null);
        setFallbackOpen(false);
      } else {
        setError(response.data.error ?? 'Could not record the fallback');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not record the fallback'));
    } finally {
      setBusy(null);
    }
  }, [taskId, authHeaders, fallbackReason, fallbackNote]);

  const copyTargetUrl = useCallback(async () => {
    if (!targetUrl) return;
    try {
      await navigator.clipboard.writeText(targetUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Could not copy the URL — copy it from the task details below.');
    }
  }, [targetUrl]);

  if (!enabled) return null;

  if (loading && !state) {
    return (
      <div className='hosted-browser-panel'>
        <div className='hosted-browser-loading'>Checking hosted browser…</div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className='hosted-browser-panel'>
        {error && <div className='hosted-browser-error'>{error}</div>}
      </div>
    );
  }

  const { capacity, session, fallback, persistenceWarning } = state;
  const remainingMs = session ? new Date(session.expiresAt).getTime() - now : 0;
  const expiringSoon = sessionRunning && remainingMs < EXPIRY_WARNING_MS;

  const capacityLabel = atCapacity
    ? 'At capacity — waiting for cleanup'
    : busy === 'open' || busy === 'relaunch'
      ? 'Starting…'
      : capacity.state === 'at_capacity'
        ? 'At capacity'
        : 'Available';

  // ── Fallback already taken: whole task now runs in the operator's incognito ──
  if (!hosted) {
    return (
      <div className='hosted-browser-panel hosted-browser-panel--fallback'>
        <div className='hosted-browser-title'>
          Working in your own incognito window
        </div>
        <div className='hosted-browser-text'>
          The hosted browser for this task was ended
          {fallback
            ? ` (${FALLBACK_REASON_LABELS[fallback.reason] ?? fallback.reason}${
                fallback.note ? ` — “${fallback.note}”` : ''
              })`
            : ''}
          . Finish the WHOLE task in a single fresh incognito window: close
          every other incognito window first, never save passwords or cards, and
          close the window when you are done.
        </div>
        {targetUrl && (
          <div className='hosted-browser-actions'>
            <button
              type='button'
              className='hosted-browser-btn'
              onClick={copyTargetUrl}
            >
              {copied ? 'Copied' : 'Copy task URL'}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='hosted-browser-panel' ref={panelRef}>
      <div className='hosted-browser-header'>
        <div className='hosted-browser-title'>Hosted browser</div>
        <div className='hosted-browser-capacity'>
          <span
            className={`hosted-browser-capacity-dot hosted-browser-capacity-dot--${
              atCapacity || capacity.state === 'at_capacity' ? 'busy' : 'ok'
            }`}
          />
          {capacityLabel} · {capacity.inUse} / {capacity.softCap} in use
        </div>
      </div>

      {!sessionRunning && (
        <div className='hosted-browser-text'>
          Do this task in a browser that belongs to this customer, so nothing
          from other customers can show up (saved cards, logins, autofill).
          Their logins are remembered for next time.
        </div>
      )}

      {error && <div className='hosted-browser-error'>{error}</div>}

      {persistenceWarning && sessionRunning && (
        <div className='hosted-browser-warning'>{persistenceWarning}</div>
      )}

      {expiringSoon && (
        <div className='hosted-browser-warning'>
          This browser closes in {formatCountdown(remainingMs)}. Finish up, or
          use Relaunch to get a fresh one with the same logins.
        </div>
      )}

      {!sessionRunning && !readOnly && (
        <div className='hosted-browser-actions'>
          <button
            type='button'
            className='hosted-browser-btn hosted-browser-btn--primary'
            onClick={() => openBrowser(false)}
            disabled={busy !== null}
          >
            {busy === 'open'
              ? 'Starting hosted browser…'
              : session
                ? 'Open hosted browser again'
                : 'Open hosted browser'}
          </button>
          <button
            type='button'
            className='hosted-browser-btn hosted-browser-btn--ghost'
            onClick={() => setFallbackOpen((v) => !v)}
            disabled={busy !== null}
          >
            Continue in my own browser (incognito)
          </button>
        </div>
      )}

      {sessionRunning && (
        <>
          <div className='hosted-browser-toolbar'>
            <span className='hosted-browser-countdown'>
              {formatCountdown(remainingMs)}
              {session?.persistsContext === false ? ' · logins not saved' : ''}
            </span>
            {!readOnly && (
              <>
                <button
                  type='button'
                  className='hosted-browser-btn'
                  onClick={reconnect}
                  disabled={busy !== null}
                  title='Reload the live view of the same browser'
                >
                  {busy === 'reconnect' ? 'Reconnecting…' : 'Reconnect'}
                </button>
                <button
                  type='button'
                  className='hosted-browser-btn'
                  onClick={() => openBrowser(true)}
                  disabled={busy !== null}
                  title='End this browser and start a fresh one with the same customer profile, opened at the task URL'
                >
                  {busy === 'relaunch' ? 'Relaunching…' : 'Relaunch'}
                </button>
                <button
                  type='button'
                  className='hosted-browser-btn'
                  onClick={release}
                  disabled={busy !== null}
                >
                  {busy === 'release' ? 'Closing…' : 'Close browser'}
                </button>
                <button
                  type='button'
                  className='hosted-browser-btn hosted-browser-btn--ghost'
                  onClick={() => setFallbackOpen((v) => !v)}
                  disabled={busy !== null}
                >
                  Continue in my own browser (incognito)
                </button>
              </>
            )}
          </div>

          {liveViewUrl ? (
            <div className='hosted-browser-live'>
              <iframe
                title='Hosted browser'
                src={liveViewUrl}
                sandbox='allow-same-origin allow-scripts allow-forms allow-popups allow-modals'
                allow='clipboard-read; clipboard-write'
              />
            </div>
          ) : (
            <div className='hosted-browser-live hosted-browser-live--empty'>
              <p>The live view is not loaded.</p>
              {!readOnly && (
                <button
                  type='button'
                  className='hosted-browser-btn hosted-browser-btn--primary'
                  onClick={reconnect}
                  disabled={busy !== null}
                >
                  Reconnect
                </button>
              )}
            </div>
          )}
        </>
      )}

      {fallbackOpen && !readOnly && (
        <div className='hosted-browser-fallback-form'>
          <div className='hosted-browser-fallback-title'>
            Move this task to your own incognito window
          </div>
          <div className='hosted-browser-text'>
            This ends the hosted browser and is logged. Use it only when the
            hosted browser cannot do the job. Then: close every other incognito
            window, open ONE fresh incognito window, do the whole task there,
            and close it when you finish. Never save passwords or cards.
          </div>
          <label className='hosted-browser-field'>
            <span>Why?</span>
            <select
              value={fallbackReason}
              onChange={(e) =>
                setFallbackReason(e.target.value as WebActionFallbackReason)
              }
              disabled={busy !== null}
            >
              {FALLBACK_REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className='hosted-browser-field'>
            <span>Details (optional)</span>
            <textarea
              rows={2}
              value={fallbackNote}
              onChange={(e) => setFallbackNote(e.target.value)}
              maxLength={500}
              disabled={busy !== null}
              placeholder='e.g. Cloudflare challenge loops after every attempt'
            />
          </label>
          <div className='hosted-browser-actions'>
            <button
              type='button'
              className='hosted-browser-btn'
              onClick={() => setFallbackOpen(false)}
              disabled={busy !== null}
            >
              Cancel
            </button>
            <button
              type='button'
              className='hosted-browser-btn hosted-browser-btn--danger'
              onClick={submitFallback}
              disabled={
                busy !== null ||
                (fallbackReason === 'other' && !fallbackNote.trim())
              }
            >
              {busy === 'fallback'
                ? 'Switching…'
                : 'End hosted browser and continue in incognito'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HostedBrowserPanel;
